import React from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {LoadingButton} from '@mui/lab';
import {useMutation, useQueryClient} from 'react-query';
import {useSnackbar} from 'notistack';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {AutomationFlow} from 'crqlar-react-common/dist/models/automation/automation';
import {AutomationService} from 'crqlar-react-common/dist/services/automation/AutomationService';
import {getErrorMessage} from 'crqlar-react-common/dist/utils/http.util';

import {serverTableQueryKey} from '../../../common/serverTable';

interface LegacyAutomationBannerProps {
    flowId?: string;
    appId?: string;
    basePath: string;
}

/** The editor serves both modules, and a migrated copy shows up in whichever list is open. */
const AUTOMATION_TABLE_IDS = ['marketing-automations', 'surveys-automations'];

// A flow with retired blocks stays editable but cannot go live or run. The migrated copy opens in a new tab,
// so the original stays open here.
const LegacyAutomationBanner: React.FC<LegacyAutomationBannerProps> = ({flowId, appId, basePath}) => {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // The tab is opened in the click and passed as the mutation variable: a tab opened after the
    // request answers is a blocked popup.
    const migrateMutation = useMutation<AutomationFlow, unknown, Window | null>({
        mutationFn: () => AutomationService.migrateFlow(flowId!),
        onSuccess: (migrated, tab) => {
            const path = `${basePath}/${appId}/${migrated.id}`;

            // Popup blocked (null): open the copy here; a later window.open would be blocked too.
            if (tab) {
                tab.location.href = `${window.location.origin}${path}`;
            } else {
                navigate(path);
            }

            enqueueSnackbar(t('Migrated copy created'), {variant: 'success'});
            // The server pauses an active original once its copy exists, so refresh this editor too.
            void queryClient.invalidateQueries(['automation', flowId]);
            AUTOMATION_TABLE_IDS.forEach(tableId => {
                void queryClient.invalidateQueries(serverTableQueryKey(tableId));
            });
        },
        onError: (error, tab) => {
            tab?.close();
            enqueueSnackbar(
                getErrorMessage(error, t('The migrated copy could not be created')),
                {variant: 'error'}
            );
        }
    });

    const handleMigrate = () => {
        if (!flowId || !appId || migrateMutation.isLoading) return;

        migrateMutation.mutate(window.open('', '_blank'));
    };

    return (
        <Alert
            severity={'warning'}
            sx={{
                flexShrink: 0,
                borderRadius: 0,
                alignItems: 'flex-start',
                '& .MuiAlert-action': {alignItems: 'center', pt: 0}
            }}
            action={(
                <LoadingButton
                    variant={'contained'}
                    color={'warning'}
                    size={'small'}
                    loading={migrateMutation.isLoading}
                    disabled={!flowId || !appId}
                    onClick={handleMigrate}
                >
                    {t('Create migrated copy')}
                </LoadingButton>
            )}
        >
            <AlertTitle>{t('This automation must be migrated')}</AlertTitle>

            <Stack gap={0.5}>
                <Typography variant={'body2'}>
                    {t('It uses building blocks that were retired (Loop, Get Profile, inline audience filters) and can no longer go live or run.')}
                </Typography>
                <Typography variant={'body2'}>
                    {t('You can recreate it manually, or create a migrated copy that you must verify before it goes live.')}
                </Typography>
            </Stack>
        </Alert>
    );
};

export default LegacyAutomationBanner;
